import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import LpnListCard from 'tio-common/components/lpn/lpn-list-card';
import LpnCard from 'tio-common/components/lpn/lpn-card';
import pageTitle from 'ember-page-title/helpers/page-title';
import { PhGlobe, PhFaders } from 'ember-phosphor-icons';
import { VStack, Section } from 'tio-ui/components/layout';
import { t, type IntlService } from 'ember-intl';
import { Paginator } from 'tio-ui/components/navigation';
import type LpnPage from 'tio-common/models/lpn-page';
import type CompanySettingModel from 'tio-common/models/company-setting';
import type AuthenticatedLpnIndexController from 'tio-employee/controllers/authenticated/lpn/index';
import { service } from '@ember/service';
import type FeaturesService from 'tio-common/services/features';
import Drawer from 'tio-reimagined/components/drawer';
import Select from 'tio-reimagined/components/select';
import Button from 'tio-reimagined/components/button';
import SearchInput from 'tio-reimagined/components/search-input';

interface LpnIndexComponentArgs {
  model: {
    recommendedPartners: LpnPage[];
    allPartners: LpnPage[];
    companySettings: CompanySettingModel;
    totalItems: number;
  };
  controller: AuthenticatedLpnIndexController;
}

const SAVINGS_OPTIONS: { key: string; labelKey: string }[] = [
  { key: 'capped_tuition', labelKey: 'lpn.savings_options.capped_tuition' },
  { key: 'family_eligible', labelKey: 'lpn.savings_options.family_eligible' },
  { key: 'no_fees', labelKey: 'lpn.savings_options.no_fees' },
];

const FORMAT_OPTIONS: { key: string; labelKey: string }[] = [
  { key: 'Online', labelKey: 'lpn.format_options.online' },
  { key: 'In-Person', labelKey: 'lpn.format_options.in_person' },
  { key: 'Hybrid', labelKey: 'lpn.format_options.hybrid' },
  { key: 'Self-Paced', labelKey: 'lpn.format_options.self_paced' },
];

const PROGRAMS_OPTIONS: { key: string; labelKey: string }[] = [
  { key: 'Associate', labelKey: 'lpn.program_options.associate' },
  { key: "Bachelor's", labelKey: 'lpn.program_options.bachelors' },
  { key: 'Boot Camp', labelKey: 'lpn.program_options.boot_camp' },
  { key: 'Certificate', labelKey: 'lpn.program_options.certificate' },
  { key: 'Doctoral', labelKey: 'lpn.program_options.doctoral' },
  { key: 'GED or HS Diploma', labelKey: 'lpn.program_options.ged_or_hs' },
  { key: "Master's", labelKey: 'lpn.program_options.masters' },
  { key: 'Short Form Course', labelKey: 'lpn.program_options.short_form_course' },
];

const AREAS_OF_STUDY_OPTIONS: { key: string; labelKey: string }[] = [
  { key: 'Accounting', labelKey: 'lpn.study_area_options.accounting' },
  { key: 'AI & Machine', labelKey: 'lpn.study_area_options.ai_machine' },
  { key: 'Applied Sciences', labelKey: 'lpn.study_area_options.applied_sciences' },
  { key: 'Business', labelKey: 'lpn.study_area_options.business' },
  { key: 'CFP®', labelKey: 'lpn.study_area_options.cfp' },
  { key: 'Computer Science', labelKey: 'lpn.study_area_options.computer_science' },
  { key: 'Construction Mgmt', labelKey: 'lpn.study_area_options.construction_mgmt' },
  { key: 'Cybersecurity', labelKey: 'lpn.study_area_options.cybersecurity' },
  { key: 'Data Science', labelKey: 'lpn.study_area_options.data_science' },
  { key: 'Education', labelKey: 'lpn.study_area_options.education' },
  { key: 'Executive Education', labelKey: 'lpn.study_area_options.executive_education' },
  { key: 'Healthcare', labelKey: 'lpn.study_area_options.healthcare' },
  { key: 'Human Resources', labelKey: 'lpn.study_area_options.human_resources' },
  { key: 'Nursing', labelKey: 'lpn.study_area_options.nursing' },
  { key: 'Project Mgmt/Six Sigma', labelKey: 'lpn.study_area_options.project_mgmt_six_sigma' },
  { key: 'Psychology', labelKey: 'lpn.study_area_options.psychology' },
  { key: 'Public Safety', labelKey: 'lpn.study_area_options.public_safety' },
  { key: 'Security & Global Studies', labelKey: 'lpn.study_area_options.security_global_studies' },
  { key: 'Social Sciences', labelKey: 'lpn.study_area_options.social_sciences' },
  { key: 'STEM', labelKey: 'lpn.study_area_options.stem' },
];

export default class LpnIndexComponent extends Component<LpnIndexComponentArgs> {
  @service declare features: FeaturesService;
  @service declare intl: IntlService;

  @tracked isFilterDrawerOpen = false;
  @tracked searchValue = this.args.controller.search ?? '';
  _searchTimer: ReturnType<typeof setTimeout> | null = null;

  static readonly SEARCH_DEBOUNCE_MS = 300;
  static readonly SEARCH_MIN_CHARS = 3;

  // Local filter state so changes aren't applied until "Apply"
  @tracked pendingSavings: string[] = [...(this.args.controller.savings ?? [])];
  @tracked pendingFormat: string[] = [...(this.args.controller.format ?? [])];
  @tracked pendingPrograms: string[] = [...(this.args.controller.programs ?? [])];
  @tracked pendingAreasOfStudy: string[] = [...(this.args.controller.areasOfStudy ?? [])];

  get isStaging() {
    const env = this.features.tioEnvironment;
    return env === 'staging' || env === 'development' || env === 'local';
  }

  get activeFilterCount(): number {
    const c = this.args.controller;
    return (
      (c.savings?.length ?? 0) +
      (c.format?.length ?? 0) +
      (c.programs?.length ?? 0) +
      (c.areasOfStudy?.length ?? 0)
    );
  }

  get filterButtonLabel(): string {
    const count = this.activeFilterCount;
    return count > 0 ? `Filters (${count})` : 'Filters';
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  changePaginatorPage = (page: number) => {
    this.args.controller.page = Math.max(1, page);
  };

  openFilterDrawer = () => {
    // Sync pending state with current applied filters
    this.pendingSavings = [...(this.args.controller.savings ?? [])];
    this.pendingFormat = [...(this.args.controller.format ?? [])];
    this.pendingPrograms = [...(this.args.controller.programs ?? [])];
    this.pendingAreasOfStudy = [...(this.args.controller.areasOfStudy ?? [])];
    this.isFilterDrawerOpen = true;
  };

  closeFilterDrawer = () => {
    this.isFilterDrawerOpen = false;
  };

  handleSearchChange = (value: string) => {
    this.searchValue = value;

    if (this._searchTimer) {
      clearTimeout(this._searchTimer);
    }

    // If cleared or below threshold, reset immediately
    if (value.length === 0) {
      this.applySearch();
      return;
    }

    if (value.length < LpnIndexComponent.SEARCH_MIN_CHARS) {
      return;
    }

    this._searchTimer = setTimeout(() => {
      this.applySearch();
    }, LpnIndexComponent.SEARCH_DEBOUNCE_MS);
  };

  handleSearchClear = () => {
    this.searchValue = '';
    this.applySearch();
  };

  applySearch = () => {
    this.args.controller.search = this.searchValue;
    this.args.controller.page = 1;
  };

  setSavings = (keys: string[]) => {
    this.pendingSavings = keys;
  };

  setFormat = (keys: string[]) => {
    this.pendingFormat = keys;
  };

  setPrograms = (keys: string[]) => {
    this.pendingPrograms = keys;
  };

  setAreasOfStudy = (keys: string[]) => {
    this.pendingAreasOfStudy = keys;
  };

  applyFilters = () => {
    const c = this.args.controller;
    c.savings = [...this.pendingSavings];
    c.format = [...this.pendingFormat];
    c.programs = [...this.pendingPrograms];
    c.areasOfStudy = [...this.pendingAreasOfStudy];
    c.page = 1;
    this.isFilterDrawerOpen = false;
  };

  clearFilters = () => {
    this.pendingSavings = [];
    this.pendingFormat = [];
    this.pendingPrograms = [];
    this.pendingAreasOfStudy = [];
  };

  clearAndApplyFilters = () => {
    const c = this.args.controller;
    c.savings = [];
    c.format = [];
    c.programs = [];
    c.areasOfStudy = [];
    c.page = 1;
    this.clearFilters();
    this.isFilterDrawerOpen = false;
  };

  <template>
    {{pageTitle (t "lpn.learning_partner_network")}}
    <VStack class="flex min-h-full flex-col p-4">
      <span class="flex items-center justify-between">
        <div class="flex items-center text-2xl text-gray-600 font-semibold">
          <PhGlobe class="mr-2" />
          {{t "lpn.learning_partner_network"}}
        </div>
        <div class="flex gap-2">
          <SearchInput
            @placeholder={{t "lpn.filters.search_placeholder"}}
            @value={{this.searchValue}}
            @onChange={{this.handleSearchChange}}
            @onClear={{this.handleSearchClear}}
          />
          <Button
            @appearance="outlined"
            @label={{this.filterButtonLabel}}
            @icon={{PhFaders}}
            {{on "click" this.openFilterDrawer}}
          />
        </div>
      </span>

      {{#if @model.allPartners.length}}
        <h2>{{t "lpn.lpn_description"}}</h2>
      {{else}}
        <h2>{{t "lpn.no_schools"}}</h2>
      {{/if}}

      {{#if @model.recommendedPartners.length}}
        <Section @headerClass="text-lg font-semibold text-gray-600">
          <:header>
            {{t "lpn.recommended"}}
          </:header>
          <:body>
            <div class="grid grid-cols-1 xl:grid-cols-3 gap-x-2 gap-y-2">
              {{#each @model.recommendedPartners as |lpn|}}
                {{#if this.isStaging}}
                  <LpnCard @lpn={{lpn}} @companySettings={{@model.companySettings}} />
                {{else}}
                  <LpnListCard @lpn={{lpn}} @companySettings={{@model.companySettings}} />
                {{/if}}
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{#if @model.allPartners.length}}
        <Section @headerClass="text-lg font-semibold text-gray-600">
          <:header>
            {{t "lpn.schools_in_network"}}
          </:header>
          <:body>
            <div class="grid grid-cols-1 xl:grid-cols-3 gap-x-2 gap-y-2">
              {{#each @model.allPartners as |lpn|}}
                {{#if this.isStaging}}
                  <LpnCard @lpn={{lpn}} @companySettings={{@model.companySettings}} />
                {{else}}
                  <LpnListCard @lpn={{lpn}} @companySettings={{@model.companySettings}} />
                {{/if}}
              {{/each}}
            </div>
            <Paginator
              @totalItems={{@model.totalItems}}
              @pageSize={{@controller.limit}}
              @currentPage={{@controller.page}}
              @onChange={{this.changePaginatorPage}}
              class="my-4"
            />
          </:body>
        </Section>
      {{/if}}
    </VStack>

    {{! ── Filter Drawer ─────────────────────────────────────────────────── }}
    <Drawer
      @isOpen={{this.isFilterDrawerOpen}}
      @onClose={{this.closeFilterDrawer}}
      @placement="right"
      @size="sm"
    >
      <:header>
        <div class="flex items-center justify-between w-full pr-8">
          <h2 class="text-lg font-semibold text-gray-900">{{t "lpn.filters.title"}}</h2>
          <button
            type="button"
            class="text-sm font-medium text-ocean-600 hover:text-ocean-800"
            {{on "click" this.clearAndApplyFilters}}
          >
            {{t "lpn.filters.clear_all"}}
          </button>
        </div>
      </:header>
      <:body>
        <div class="space-y-6">
          <Select
            @label={{t "lpn.filters.savings"}}
            @items={{this.savingsOptions}}
            @multiple={{true}}
            @selectedKeys={{this.pendingSavings}}
            @onChangeKeys={{this.setSavings}}
            @placeholder={{t "lpn.filters.savings_placeholder"}}
          />
          <Select
            @label={{t "lpn.filters.format"}}
            @items={{this.formatOptions}}
            @multiple={{true}}
            @selectedKeys={{this.pendingFormat}}
            @onChangeKeys={{this.setFormat}}
            @placeholder={{t "lpn.filters.format_placeholder"}}
          />
          <Select
            @label={{t "lpn.filters.programs"}}
            @items={{this.programsOptions}}
            @multiple={{true}}
            @selectedKeys={{this.pendingPrograms}}
            @onChangeKeys={{this.setPrograms}}
            @placeholder={{t "lpn.filters.programs_placeholder"}}
          />
          <Select
            @label={{t "lpn.filters.areas_of_study"}}
            @items={{this.areasOfStudyOptions}}
            @multiple={{true}}
            @selectedKeys={{this.pendingAreasOfStudy}}
            @onChangeKeys={{this.setAreasOfStudy}}
            @placeholder={{t "lpn.filters.areas_of_study_placeholder"}}
          />
        </div>
      </:body>
      <:footer>
        <div class="flex gap-3 justify-end">
          <Button
            @appearance="outlined"
            @label={{t "lpn.filters.cancel"}}
            {{on "click" this.closeFilterDrawer}}
          />
          <Button
            @intent="primary"
            @label={{t "lpn.filters.apply"}}
            {{on "click" this.applyFilters}}
          />
        </div>
      </:footer>
    </Drawer>
  </template>

  // ── Translated option lists ─────────────────────────────────────────

  private _translateOptions(options: { key: string; labelKey: string }[]) {
    return options.map((o) => ({ key: o.key, label: this.intl.t(o.labelKey) }));
  }

  get savingsOptions() {
    return this._translateOptions(SAVINGS_OPTIONS);
  }

  get formatOptions() {
    return this._translateOptions(FORMAT_OPTIONS);
  }

  get programsOptions() {
    return this._translateOptions(PROGRAMS_OPTIONS);
  }

  get areasOfStudyOptions() {
    return this._translateOptions(AREAS_OF_STUDY_OPTIONS);
  }
}
